import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, Menu, X, Phone, Mail, MessageCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import type { SiteSettings } from '../types';

const whatsappHref = (w: string) => (w.startsWith('http') ? w : `https://wa.me/${w.replace(/[^+\d]/g, '')}`);

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [settings, setSettings] = useState<SiteSettings>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get<SiteSettings>('/settings').then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const logo = settings.logo_url || null;
  const brandName = settings.company_name;
  const whatsapp = settings.whatsapp ? whatsappHref(settings.whatsapp) : null;

  const offerings = [
    { to: '/sales', label: 'Sales' },
    { to: '/leasing', label: 'Leasing' },
    { to: '/management', label: 'Management' },
  ];

  const close = () => {
    setIsMobileOpen(false);
    setIsDropdownOpen(false);
  };

  const navLinkClass =
    'uppercase text-sm md:text-[15px] font-medium tracking-widest text-white hover:text-primary transition-colors';

  return (
    <header className="absolute inset-x-0 top-0 z-50 bg-gradient-to-b from-black/80 via-black/40 to-transparent border-b border-transparent">
      {/* Main nav */}
      <nav>
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative flex items-center justify-between h-28 md:h-32">
            {/* Logo - left */}
            <Link to="/" className="flex items-center shrink-0" onClick={close}>
              {logo ? (
                <img
                  src={logo}
                  alt={brandName}
                  className="h-16 md:h-20 w-auto max-w-[40vw] md:max-w-[280px] object-contain"
                />
              ) : (
                <span className="font-display text-lg md:text-2xl font-bold text-white uppercase tracking-widest">
                  {brandName}
                </span>
              )}
            </Link>

            {/* Desktop right nav */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={navLinkClass} onClick={close}>Home</Link>
              <Link to="/about" className={navLinkClass}>About Us</Link>
              <Link to="/contact" className={navLinkClass}>Contact</Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  className={`flex items-center ${navLinkClass} focus:outline-none`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  Core Offerings
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-4 w-64 bg-charcoal rounded-xl shadow-2xl ring-1 ring-primary/40 py-2 z-50">
                    <div className="px-4 py-2 text-[11px] uppercase tracking-widest text-taupe font-semibold border-b border-primary/20 mb-1">
                      Core Offerings
                    </div>
                      {offerings.map((o) => (
                        <Link
                          key={o.to}
                          to={o.to}
                          className="block px-4 py-3 text-white hover:bg-primary/10 hover:text-primary transition-colors uppercase text-sm tracking-wide"
                          onClick={close}
                        >
                          {o.label}
                        </Link>
                      ))}
                  </div>
                )}
              </div>

              <Link
                to="/blog"
                className={navLinkClass}
                onClick={close}
              >
                Blog
              </Link>
            </div>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2 text-primary self-start mt-4 active:scale-95 transition"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              aria-label="Toggle menu"
            >
              {isMobileOpen ? <X className="h-8 w-8" strokeWidth={2.5} /> : <Menu className="h-8 w-8" strokeWidth={3} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu - full screen overlay */}
      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 top-0 bg-obsidian z-50 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4">
            <div className="relative flex items-center justify-between h-28">
              {logo ? (
                <img src={logo} alt={brandName} className="h-14 w-auto max-w-[45vw] object-contain" />
              ) : (
                <span className="font-display text-lg font-bold text-white uppercase tracking-widest">
                  {brandName}
                </span>
              )}
              <button
                className="p-2 text-primary"
                onClick={() => setIsMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-8 w-8" strokeWidth={2.5} />
              </button>
            </div>
            <nav className="pt-4 pb-10">
              <div className="space-y-1">
                <Link to="/" className="block px-2 py-3 text-white hover:text-primary uppercase text-base tracking-wide border-b border-white/10" onClick={close}>Home</Link>
                <Link to="/about" className="block px-2 py-3 text-white hover:text-primary uppercase text-base tracking-wide border-b border-white/10" onClick={close}>About Us</Link>
                <Link to="/contact" className="block px-2 py-3 text-white hover:text-primary uppercase text-base tracking-wide border-b border-white/10" onClick={close}>Contact</Link>
                {offerings.map((o) => (
                  <Link key={o.to} to={o.to} className="block px-2 py-3 text-white hover:text-primary uppercase text-base tracking-wide border-b border-white/10" onClick={close}>
                    {o.label}
                  </Link>
                ))}
                <Link to="/blog" className="block px-2 py-3 text-white hover:text-primary uppercase text-base tracking-wide border-b border-white/10" onClick={close}>Blog</Link>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                {settings.phone && (
                  <a href={`tel:${settings.phone.replace(/[^+\d]/g, '')}`} className="flex items-center px-2 py-2 text-taupe hover:text-primary text-sm tracking-wide">
                    <Phone className="h-4 w-4 text-primary mr-2 shrink-0" /> {settings.phone}
                  </a>
                )}
                {settings.email && (
                  <a href={`mailto:${settings.email}`} className="flex items-center px-2 py-2 text-taupe hover:text-primary text-sm tracking-wide lowercase">
                    <Mail className="h-4 w-4 text-primary mr-2 shrink-0" /> {settings.email}
                  </a>
                )}
              </div>

              {whatsapp && (
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 flex items-center justify-center bg-primary text-black px-6 py-3 uppercase text-base font-semibold tracking-widest hover:bg-primary-light transition-colors"
                >
                  Chat on WhatsApp <MessageCircle className="ml-2 h-5 w-5" />
                </a>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
